import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type { ContaAzulRow, ParseError, ParseResult } from '../types';

/**
 * Normaliza string (header, nome de categoria, CC) removendo acentos,
 * espaços extras e mudando para lowercase para matching tolerante.
 * Exportado para reuso em getCategoryByName/getCostCenterByName/importBatch.
 */
export function normalizeName(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

const normalizeHeader = normalizeName;

const HEADER_ALIASES: Record<string, string[]> = {
  dueDate: [
    'data de vencimento',
    'vencimento',
    // Faturas de cartão: a coluna de data é "data da compra" / "data" / "dia"
    'data da compra',
    'data',
    'dia',
  ],
  paymentDate: [
    'data do último pagamento',
    'data do ultimo pagamento',
    'data de pagamento',
    'pagamento',
    'data pgto',
  ],
  description: [
    'descricao',
    'descrição',
    'historico',
    'histórico',
    // Faturas de cartão usam "estabelecimento" / "descrição da compra"
    'estabelecimento',
    'nome do estabelecimento',
    'descrição da compra',
    'descricao da compra',
  ],
  amount: [
    'valor original da parcela (r$)',
    'valor original da parcela',
    'valor (r$)',
    'valor r$',
    'valor',
    'total',
  ],
  categoryName: ['categoria 1', 'categoria'],
  costCenterName: [
    'centro de custo 1',
    'centro de custo',
    'centro custo',
    'centro de custos',
    'centro de custo 2',
    'centro de custo 3',
  ],
  notes: [
    'nome do fornecedor',
    'nome do cliente',
    'observações',
    'observacoes',
    'cliente / fornecedor',
    'cliente/fornecedor',
    'cliente',
    'fornecedor',
    // Faturas de cartão trazem parcelamento útil como anotação
    'parcela',
    'parcelas',
    'parc.',
    'segmento',
  ],
  status: ['situação', 'situacao', 'status'],
};

function findColumn(headers: string[], logicalField: string): string | null {
  const aliases = HEADER_ALIASES[logicalField] ?? [];
  const normalizedHeaders = headers.map(normalizeHeader);
  for (const alias of aliases) {
    const idx = normalizedHeaders.indexOf(normalizeHeader(alias));
    if (idx >= 0) return headers[idx]!;
  }
  return null;
}

/**
 * Parseia valor BR: "R$ 1.234,56" | "1.234,56" | "1234.56" | "1234,56" → 1234.56
 */
function parseBrazilianNumber(raw: string): number | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[R$\s]/g, '').trim();
  if (!cleaned) return null;
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  let normalized: string;
  if (hasComma && hasDot) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    normalized = cleaned.replace(',', '.');
  } else {
    normalized = cleaned;
  }
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Parseia data BR: "DD/MM/YYYY" | "YYYY-MM-DD" → Date
 */
function parseBrazilianDate(raw: string): Date | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const brMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (brMatch) {
    const [, d, m, y] = brMatch;
    const date = new Date(parseInt(y!), parseInt(m!) - 1, parseInt(d!));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const date = new Date(parseInt(y!), parseInt(m!) - 1, parseInt(d!));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

const COMPLETED_STATUSES = ['pago', 'recebido', 'liquidado', 'baixado'];

function mapStatus(raw: string | undefined): 'completed' | 'pending' {
  if (!raw) return 'pending';
  const normalized = normalizeHeader(raw);
  return COMPLETED_STATUSES.includes(normalized) ? 'completed' : 'pending';
}

/**
 * Validação compartilhada — recebe linhas já parseadas (CSV ou XLSX) e produz ParseResult.
 */
function processRows(records: Record<string, string>[], headers: string[]): ParseResult {
  const rows: ContaAzulRow[] = [];
  const errors: ParseError[] = [];
  const uniqueCategories = new Set<string>();
  const uniqueCostCenters = new Set<string>();

  if (records.length === 0) {
    errors.push({ line: 0, reason: 'Arquivo vazio ou sem linhas de dados.' });
    return { rows, errors, uniqueCategories: [], uniqueCostCenters: [] };
  }

  const dueDateCol = findColumn(headers, 'dueDate');
  const descriptionCol = findColumn(headers, 'description');
  const amountCol = findColumn(headers, 'amount');
  const paymentDateCol = findColumn(headers, 'paymentDate');
  const categoryCol = findColumn(headers, 'categoryName');
  const costCenterCol = findColumn(headers, 'costCenterName');
  const notesCol = findColumn(headers, 'notes');
  const statusCol = findColumn(headers, 'status');

  if (!dueDateCol || !descriptionCol || !amountCol) {
    errors.push({
      line: 0,
      reason: `Colunas obrigatórias ausentes. Precisa ter: Data de Vencimento, Descrição e Valor. Encontrado: ${headers.join(', ')}`,
    });
    return { rows, errors, uniqueCategories: [], uniqueCostCenters: [] };
  }

  records.forEach((record, idx) => {
    const lineNumber = idx + 2;
    const dueDateRaw = record[dueDateCol] ?? '';
    const descriptionRaw = record[descriptionCol] ?? '';
    const amountRaw = record[amountCol] ?? '';

    const dueDate = parseBrazilianDate(dueDateRaw);
    if (!dueDate) {
      errors.push({ line: lineNumber, reason: `Data de vencimento inválida: "${dueDateRaw}"` });
      return;
    }

    const description = descriptionRaw.trim();
    if (!description) {
      errors.push({ line: lineNumber, reason: 'Descrição vazia' });
      return;
    }

    const amount = parseBrazilianNumber(amountRaw);
    if (amount === null || amount < 0) {
      errors.push({ line: lineNumber, reason: `Valor inválido: "${amountRaw}"` });
      return;
    }

    const paymentDate = paymentDateCol
      ? (parseBrazilianDate(record[paymentDateCol] ?? '') ?? undefined)
      : undefined;

    const categoryName = categoryCol ? (record[categoryCol] ?? '').trim() || undefined : undefined;
    const costCenterName = costCenterCol
      ? (record[costCenterCol] ?? '').trim() || undefined
      : undefined;
    const notes = notesCol ? (record[notesCol] ?? '').trim() || undefined : undefined;
    const statusRaw = statusCol ? record[statusCol] : undefined;
    const status = mapStatus(statusRaw);

    if (categoryName) uniqueCategories.add(categoryName);
    if (costCenterName) uniqueCostCenters.add(costCenterName);

    const row: ContaAzulRow = {
      lineNumber,
      dueDate,
      description,
      amount,
      status,
      ...(paymentDate && { paymentDate }),
      ...(categoryName && { categoryName }),
      ...(costCenterName && { costCenterName }),
      ...(notes && { notes }),
    };
    rows.push(row);
  });

  return {
    rows,
    errors,
    uniqueCategories: Array.from(uniqueCategories).sort(),
    uniqueCostCenters: Array.from(uniqueCostCenters).sort(),
  };
}

export function parseContaAzulCSV(text: string): ParseResult {
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    delimiter: '',
    dynamicTyping: false,
    transformHeader: (h) => h.trim(),
  });

  const records = parsed.data ?? [];
  const headers = parsed.meta.fields ?? [];
  return processRows(records, headers);
}

/**
 * Lê a primeira sheet do XLSX e converte em records compatíveis com processRows.
 * Usa raw:false para que números/datas saiam formatados como strings BR (e.g. "15/03/2026", "R$ 3.500,00").
 */
export function parseContaAzulXLSX(buffer: ArrayBuffer): ParseResult {
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  } catch (e) {
    return {
      rows: [],
      errors: [
        {
          line: 0,
          reason: `Erro ao ler arquivo Excel: ${e instanceof Error ? e.message : String(e)}`,
        },
      ],
      uniqueCategories: [],
      uniqueCostCenters: [],
    };
  }

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    return {
      rows: [],
      errors: [{ line: 0, reason: 'Arquivo Excel sem planilhas.' }],
      uniqueCategories: [],
      uniqueCostCenters: [],
    };
  }

  const sheet = workbook.Sheets[firstSheetName];
  if (!sheet) {
    return {
      rows: [],
      errors: [{ line: 0, reason: 'Primeira planilha não encontrada.' }],
      uniqueCategories: [],
      uniqueCostCenters: [],
    };
  }

  // raw:false formata datas/números como strings (e.g. "15/03/2026", "3500"); defval garante string vazia para células vazias
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' });
  if (aoa.length === 0) {
    return {
      rows: [],
      errors: [{ line: 0, reason: 'Planilha vazia.' }],
      uniqueCategories: [],
      uniqueCostCenters: [],
    };
  }

  // Detecta linha de cabeçalho: primeira linha que contém pelo menos uma das colunas obrigatórias
  let headerRowIdx = 0;
  for (let i = 0; i < Math.min(aoa.length, 10); i++) {
    const candidate = (aoa[i] ?? []).map((v) => String(v ?? '').trim());
    const normalized = candidate.map(normalizeHeader);
    const hasDueDate = HEADER_ALIASES['dueDate']!.some((a) =>
      normalized.includes(normalizeHeader(a)),
    );
    const hasDesc = HEADER_ALIASES['description']!.some((a) =>
      normalized.includes(normalizeHeader(a)),
    );
    if (hasDueDate && hasDesc) {
      headerRowIdx = i;
      break;
    }
  }

  const headerRow = (aoa[headerRowIdx] ?? []).map((v) => String(v ?? '').trim());
  const dataRows = aoa.slice(headerRowIdx + 1);

  const records: Record<string, string>[] = dataRows
    .filter((row) => row.some((cell) => String(cell ?? '').trim() !== ''))
    .map((row) => {
      const obj: Record<string, string> = {};
      headerRow.forEach((h, i) => {
        if (h) obj[h] = String(row[i] ?? '').trim();
      });
      return obj;
    });

  return processRows(
    records,
    headerRow.filter((h) => h),
  );
}

/**
 * Detecta o formato pelo nome do arquivo e dispatcha para o parser correto.
 */
export async function parseContaAzulFile(file: File): Promise<ParseResult> {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
    const buffer = await file.arrayBuffer();
    return parseContaAzulXLSX(buffer);
  }
  // Default: CSV
  const text = await file.text();
  return parseContaAzulCSV(text);
}
